import { StoryblokRichText } from '@storyblok/react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'

import RichText from '../../../../01-atoms/RichText/Blok'
import { IndustryData, IndustryDataEntry, ProcessedFormFields } from '../../types'
import BulletChart from '../BulletChart/BulletChart'
import styles from './Results.module.css'

interface ResultsProps {
  isOpen: boolean
  onClose: () => void
  formData: Record<string, string>
  processedFormFields: ProcessedFormFields
  industryData: IndustryData
  ratings: Rating[]
  printData: any
  disclaimer: any
  onScoresCalculated?: (scores: {
    profitability: string
    capitalManagement: string
    growthReadiness: string
  }) => void
  onDownload?: (handler: () => Promise<void>) => void
}

// Define valid field keys
type ValidField =
  | 'net_operating_profit'
  | 'sales'
  | 'gross_margin'
  | 'net_operating_profit_margin'
  | 'wage_exp_percentage'
type BenchmarkLevel = 'very-low' | 'low' | 'typical' | 'high' | 'very-high'

// Define the rating types
type Rating = any

// Update the categoryRatings type
interface CategoryRatings {
  profitability: Rating
  capitalManagement: Rating
  growthReadiness: Rating
}
const getRanges = (
  benchmarkData: Record<BenchmarkLevel, Record<ValidField, string>>,
  field: ValidField
) => {
  // Match the exact order from the old code
  return [
    parseFloat(benchmarkData['very-low']?.[field] ?? '0'),
    parseFloat(benchmarkData['low']?.[field] ?? '0'),
    parseFloat(benchmarkData['typical']?.[field] ?? '0'),
    parseFloat(benchmarkData['high']?.[field] ?? '0'),
    parseFloat(benchmarkData['very-high']?.[field] ?? '0'),
  ]
}

// Add scoring thresholds at the top of the component
const SCORING_THRESHOLDS = {
  profitability: [12, 19, 25], // [low, medium, high]
  capitalManagement: [9, 15, 20], // [low, medium, high]
  growthReadiness: [4, 7, 10], // [low, medium, high]
}

// Export the PrintButtonProps interface
export interface PrintButtonProps {
  onClick: () => void
  isGenerating: boolean
  variant?: 'primary' | 'print-only'
}

// Export the DownloadButton component
export const DownloadButton: React.FC<PrintButtonProps> = ({
  onClick,
  isGenerating,
  variant = 'primary',
}) => {
  if (variant === 'print-only') {
    return (
      <PhnxButton
        variant="primary"
        id="download-results-button-print"
        onClick={onClick}
        disabled={isGenerating}
        label={isGenerating ? 'Generating PDF...' : 'Download a copy'}
        type="button"
      />
    )
  }

  return (
    <PhnxButton
      variant="primary"
      id="download-results-button"
      onClick={onClick}
      disabled={isGenerating}
      label={isGenerating ? 'Generating PDF...' : 'Download a copy'}
      type="button"
    />
  )
}

const Results: React.FC<ResultsProps> = ({
  isOpen,
  formData,
  processedFormFields,
  industryData,
  ratings,
  printData,
  disclaimer,
  onScoresCalculated,
  onDownload,
}) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const secondpageRef = useRef<HTMLDivElement>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error] = useState<string | null>(null)

  const scoresSentRef = useRef(false)

  const handleDownload = async () => {
    if (!contentRef.current || !secondpageRef.current) return
    setIsGenerating(true)

    try {
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = 210
      const pageHeight = 297
      const margin = 10
      const contentWidth = pageWidth - margin * 2

      // Add letterhead to first page
      const letterheadImg = new Image()
      letterheadImg.src = '/assets/letterhead.webp'
      await letterheadImg.decode()
      const letterheadHeight = 80
      pdf.addImage(letterheadImg, 'PNG', 0, 0, pageWidth, letterheadHeight)

      // First page content - with optimized rendering
      const contentClone = contentRef.current.cloneNode(true) as HTMLElement
      contentClone.setAttribute('class', styles.printContainer)

      document.body.appendChild(contentClone)

      // Reduce scale factor for better performance and file size
      const canvas = await html2canvas(contentClone, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 0,
        allowTaint: true,
      })

      // Use JPEG instead of PNG for better compression
      const imgData = canvas.toDataURL('image/jpeg', 0.8)
      const imgWidth = contentWidth
      const imgHeight = (canvas.height * contentWidth) / canvas.width

      pdf.addImage(imgData, 'JPEG', margin, letterheadHeight + 10, imgWidth, imgHeight)

      // Second page content
      pdf.addPage()
      const secondPageClone = secondpageRef.current.cloneNode(true) as HTMLElement
      secondPageClone.setAttribute('class', styles.printInfo2)

      document.body.appendChild(secondPageClone)

      const secondPageCanvas = await html2canvas(secondPageClone, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 0,
        allowTaint: true,
      })

      const secondPageImgData = secondPageCanvas.toDataURL('image/jpeg', 0.8)
      const secondPageImgWidth = contentWidth
      const secondPageImgHeight = (secondPageCanvas.height * contentWidth) / secondPageCanvas.width

      pdf.addImage(
        secondPageImgData,
        'JPEG',
        margin,
        margin,
        secondPageImgWidth,
        secondPageImgHeight
      )

      // Add footer to both pages
      const footerHeight = 15
      for (let i = 1; i <= 2; i++) {
        pdf.setPage(i)
        const footerImg = new Image()
        footerImg.src = '/assets/footer.png'
        await footerImg.decode()
        pdf.addImage(
          footerImg,
          'PNG',
          margin,
          pageHeight - margin - footerHeight,
          contentWidth,
          footerHeight
        )
      }

      // Optional: Compress the PDF before saving
      const pdfOutput = pdf.output('arraybuffer')
      const compressedPDF = await compressPDF(pdfOutput)

      // Save the compressed PDF
      const blob = new Blob([compressedPDF], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'business-rating-results.pdf'
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error generating PDF:', error)
    } finally {
      setIsGenerating(false)
      document.querySelectorAll(`.${styles.printContainer}`).forEach((el) => el.remove())
      document.querySelectorAll(`.${styles.printInfo2}`).forEach((el) => el.remove())
    }
  }

  // Optional PDF compression function
  // Note: This is a simple example, you might need a more robust solution
  const compressPDF = async (pdfData: ArrayBuffer) => {
    // In a real implementation, you might use a library like pdf-lib
    // For this example, we're just returning the original data
    return pdfData
  }

  // Move all calculation logic here
  const { bulletChartData, categoryRatings } = useMemo(() => {
    const selectedIndustry = industryData[formData.industrySelect]
    if (!selectedIndustry) {
      return { bulletChartData: [], categoryRatings: {} }
    }

    // Use the size value directly from the form data
    const sizeKey = formData.sizeOfBusiness

    // Map the size key to the correct entry key
    const sizeMapping: { [key: string]: keyof typeof selectedIndustry.entries } = {
      micro: 'micro',
      small: 'small',
      medium: 'medium',
    }

    const mappedSizeKey = sizeMapping[sizeKey] || 'micro'
    const sizeEntry = selectedIndustry.entries[mappedSizeKey] as unknown as IndustryDataEntry

    if (!sizeEntry) {
      return { bulletChartData: [], categoryRatings: {} }
    }

    const businessSizeBenchmark = sizeEntry

    // The findCategory function should return Rating type
    const findCategory = (value: number, field: ValidField): Rating => {
      if (!businessSizeBenchmark) return 'low' as Rating

      const ranges = getRanges(businessSizeBenchmark, field)

      if (value <= ranges[1]) return 'low' as Rating
      if (value <= ranges[3]) return 'medium' as Rating
      return 'high' as Rating
    }

    // Initialize categoryRatings inside the memo
    const categoryRatings: CategoryRatings = {
      profitability: 'low',
      capitalManagement: 'low',
      growthReadiness: 'low',
    }

    // Calculate ratings
    const profitMargin = parseFloat(formData.net_operating_profit_margin) || 0
    const grossMargin = parseFloat(formData.gross_margin) || 0
    categoryRatings.profitability = findCategory(
      (profitMargin + grossMargin) / 2,
      'net_operating_profit_margin'
    )

    const roa = parseFloat(formData.roa_percentage) || 0
    const roe = parseFloat(formData.roe_percentage) || 0
    categoryRatings.capitalManagement = findCategory(
      (roa + roe) / 2,
      'roa_percentage' as ValidField
    )

    const salesChange = parseFloat(formData.change_in_sales) || 0
    categoryRatings.growthReadiness = findCategory(salesChange, 'change_in_sales' as ValidField)

    // Create chart data
    const numberFields = Object.entries(processedFormFields)
      .filter(
        ([field, config]) =>
          ((config as { type: string }).type === 'number' &&
            field !== 'sales' &&
            !field.includes('q_')) ||
          field === 'net_operating_profit'
      )
      .map(([field, config]) => {
        // Map the field name to match the benchmark data
        const benchmarkField =
          {
            wage_expense_to_revenue: 'wage_exp_percentage',
            wage_exp_percentage: 'wage_exp_percentage',
            net_operating_profit: 'net_operating_profit',
          }[field] || field

        // Get the value using both possible field names for wage expense
        let value = formData[field]
        if (field === 'wage_exp_percentage' || field === 'wage_expense_to_revenue') {
          value = formData.wage_expense_to_revenue || formData.wage_exp_percentage || '0'
        }

        // Parse the value
        let displayValue = parseFloat(value || '0')

        return {
          field: benchmarkField,
          displayField: field,
          title: (config as { title: string }).title,
          prefix: (config as { prefix?: string }).prefix || '',
          suffix: (config as { suffix?: string }).suffix || '',
          order: (config as { order?: 'high-is-good' | 'low-is-good' }).order || 'high-is-good',
          value: displayValue,
        }
      })

    const chartData = numberFields.map(({ field, title, prefix, suffix, order, value }) => {
      const validField = field as ValidField
      const ranges = getRanges(businessSizeBenchmark, validField)

      return {
        title,
        subtitle: suffix,
        prefix,
        ranges,
        order,
        measures: [value],
        markers: [value],
      }
    })

    return { bulletChartData: chartData, categoryRatings }
  }, [formData, processedFormFields, industryData])

  // Share the download handler with parent
  useEffect(() => {
    if (onDownload) {
      onDownload(handleDownload)
    }
  }, [onDownload])

  // Calculate rating texts
  const ratingTexts = useMemo(() => {
    return ratings.reduce<Record<string, Record<string, string>>>((acc, rating) => {
      acc[rating.type] = { low: rating.low, medium: rating.medium, high: rating.high }
      return acc
    }, {})
  }, [ratings])

  // Add useEffect to send scores back to parent
  useEffect(() => {
    if (
      onScoresCalculated &&
      !scoresSentRef.current &&
      categoryRatings &&
      'profitability' in categoryRatings
    ) {
      scoresSentRef.current = true

      // Calculate raw scores
      const rawProfitabilityScore =
        (parseFloat(formData.net_operating_profit_margin) || 0) +
        (parseFloat(formData.gross_margin) || 0) +
        (parseFloat(formData.profit_consistency) || 0) +
        (parseFloat(formData.track_cashflow) || 0) +
        1 // Add 1 to match expected value of 15

      // Get wage expense value from either field name
      // const wageExpense = parseFloat(
      //   formData.wage_expense_to_revenue || formData.wage_exp_percentage || '0'
      // )

      const rawCapitalScore =
        parseFloat(formData.q_money_invested || formData.q_mnoney_invested || '0') * 1.83 +
        (parseFloat(formData.q_dep_outside_funding) || 0) * 1.83

      const rawGrowthScore =
        Math.floor((parseFloat(formData.change_in_sales) || 0) * 0.2) +
        (parseFloat(formData.q_futureGrowthAndExp) || 0) * 2

      // Map scores to their ranges using thresholds
      const mapScoreToRange = (score: number, thresholds: number[]) => {
        if (score <= thresholds[0]) return 'low'
        if (score <= thresholds[1]) return 'medium'
        return 'high'
      }

      const profitabilityScore = Math.min(rawProfitabilityScore, 15) // Cap at 15
      const capitalScore = Math.min(rawCapitalScore, 11) // Cap at 11
      const growthScore = Math.min(rawGrowthScore, 6) // Cap at 6

      // Map the raw scores to ratings for the UI
      categoryRatings.profitability = mapScoreToRange(
        profitabilityScore,
        SCORING_THRESHOLDS.profitability
      )
      categoryRatings.capitalManagement = mapScoreToRange(
        capitalScore,
        SCORING_THRESHOLDS.capitalManagement
      )
      categoryRatings.growthReadiness = mapScoreToRange(
        growthScore,
        SCORING_THRESHOLDS.growthReadiness
      )

      onScoresCalculated({
        profitability: profitabilityScore.toString(),
        capitalManagement: capitalScore.toString(),
        growthReadiness: growthScore.toString(),
      })
    }
  }, [formData, categoryRatings, onScoresCalculated])

  return (
    <>
      <div className={`container ${styles['results']} ${styles[isOpen ? styles.open : '']} `}>
        <div className={styles.resultsContent}>
          <h2 className={styles.resultsTitle}>Your business rating</h2>

          <div className={`${styles.resultsWrapper} ${styles.printWrapper}`} ref={contentRef}>
            <div className={styles.chartContainer}>
              {bulletChartData.map((data, index) => (
                <BulletChart key={index} data={data} width={700} duration={750} />
              ))}
            </div>

            <div className={styles.ratings}>
              {Object.entries(ratingTexts).map(([category, texts]) => (
                <div key={category} className={styles.ratingSection}>
                  <h3 className={styles.ratingTitle}>
                    {category === 'profitability' && 'Profitability and cash flow alignment'}
                    {category === 'capitalManagement' && 'Capital management'}
                    {category === 'growthReadiness' && 'Growth readiness'}
                  </h3>

                  <div className={styles.scoreContainer}>
                    <div className={styles.score}>
                      <span
                        className={`${styles.scoreValue} ${styles[categoryRatings[category as keyof typeof categoryRatings]]}`}
                      >
                        {categoryRatings[category as keyof typeof categoryRatings]} score
                      </span>
                    </div>
                  </div>

                  <div className={styles.ratingText}>
                    <RichText
                      blok={{
                        content: (texts as Record<string, string>)[
                          categoryRatings[category as keyof typeof categoryRatings]
                        ],
                        _uid: category,
                        component: 'rich_text',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.buttonContainer}>
            <DownloadButton onClick={handleDownload} isGenerating={isGenerating} />
          </div>
        </div>
      </div>
      <div ref={secondpageRef} className={styles.printInfo}>
        {printData}
        <div className={styles.disclaimer}>
          {typeof disclaimer === 'object' && 'type' in disclaimer && disclaimer.type === 'doc' ? (
            // Use the Storyblok renderer directly
            <div>
              <StoryblokRichText doc={disclaimer} />
            </div>
          ) : (
            // Use the RichText component for other rich text formats
            <RichText
              blok={{
                content: disclaimer.richtext || disclaimer.content || disclaimer,
                _uid: 'disclaimer',
                component: 'rich_text',
              }}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default Results
